import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineArrowSmLeft, HiOutlineShoppingCart } from "react-icons/hi";
import ListProduct from "./Component/ListProduct";
import ListOrder from "./Component/ListOrder";
import { orderType } from "@/config";
import { useAppContext } from "@/use-context/Context";
import { apiGetOrder } from "@/services/ServiceApi";
import { getShop } from "@/utils/auth";
import empty from "@/assets/images/restaurant 1.png";
import { useTranslation } from "react-i18next";
import { SocketEvent } from "@/enum/socketEvent";
import record from "@/assets/Bell.mp3";
import { handleSocketEvent } from "./Component/Message/handleSocketEvent";
import { handleSocketEventCustomer } from "./Component/Message/handleSocketEventCustomer";
import socket from "@/utils/socket";
import { handleSocketStatusProductIn } from "./Component/Message/handleSocketStatusProductIn";
import Homepage from "./Component/Mobile/Homepage";
import CartDrawer from "./Component/Drawer/CartDrawer";
import { useFetchBusinessSetting } from "@/components/hooks/useFetchBusinessSetting";
import TopBar from "@/components/Themes/Rubick/TopBar";

const OrderByPOS = () => {
    useFetchBusinessSetting();
    const shop = getShop();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const sound = new Audio(record);
    const { listItems, setListItems, setAddToCart, fetchProduct, addToCart, loading } = useAppContext();

    const tableId = localStorage.getItem("tableId");
    const tableName = localStorage.getItem("tableName");

    const [isCate, setIsCate] = useState<boolean>(false);
    const [isCart, setIsCart] = useState<boolean>(false);
    const [orderObj, setOrderObj] = useState<orderType[]>([]);
    const [contentHeight, setContentHeight] = useState<number>(window.innerHeight);

    const totalQuantity = addToCart.reduce((total, item) => total + item.quantity, 0) || 0;

    const orderBack = () => {
        navigate("/");
        localStorage.setItem('textSearch', "");
        localStorage.setItem("categoryId", "");
        localStorage.setItem("selectAll", "false");
        localStorage.setItem("tableId", "");
        const updatedItems = listItems.map(item => ({
            ...item,
            quantity: 0
        }));
        setListItems(updatedItems)
        setAddToCart([]);
    };

    useEffect(() => {
        fetchProduct();
    }, []);

    const fetchOrder = (id: string) => {
        apiGetOrder(id, { productType: "1,2" })
            .then(response => {
                if (response.success) {
                    const orders = response.data;
                    setOrderObj(orders);
                    const result = orders?.flatMap((order: any) =>
                        order.orderDetails?.length
                            ? order.orderDetails.map((detail: any) => ({
                                _id: detail._id,
                                orderId: detail.orderId,
                                businessId: detail.businessId,
                                tableId: order.tableId,
                                description: detail.product?.description,
                                descriptionKH: detail.product?.descriptionKH,
                                name: detail.name,
                                nameKH: detail.nameKH,
                                price: detail.fullPrice,
                                quantity: detail.quantity,
                                status: detail.productStatus,
                                image: detail.product?.image,
                                tracking: detail.statusTracking,
                                instruction: detail.instruction,
                                categories: detail.product?.categories,
                                addonIds: detail.product?.addonIds,
                                optionIds: detail.product?.optionIds,
                                discountPercent: detail.discountPercent,
                                isTrackStock: detail.product?.isTrackStock,
                                requiresCooking: detail.product?.requiresCooking,
                                isReturnable: detail.product?.isReturnable,
                                inventoryStock: detail.product?.inventoryStock,
                                options: detail.options?.map((opt: any) => ({
                                    ...opt,
                                    values: opt.values?.map((val: any) => ({
                                        ...val,
                                        isCheck: true,
                                    })) ?? [],
                                })) ?? [],
                                addons: detail.addons ?? [],
                            }))
                            : []
                    ) ?? [];

                    setAddToCart((prevCart) => {
                        if (!result.length) {
                            return prevCart.filter(item => item.isWaiter);
                        }

                        const resultMap = new Map(result.map(item => [item._id, item]));

                        const updatedCart = prevCart
                            .map((item) => {
                                const found = resultMap.get(item._id);

                                if (found) {
                                    return {
                                        ...item,
                                        ...found,
                                        quantity: found.quantity,
                                        status: found.status,
                                    };
                                }

                                if (item.isWaiter) {
                                    return item;
                                }

                                return null;
                            })
                            .filter(Boolean);

                        const newItems = result.filter(
                            (r) => !prevCart.some((item) => item._id === r._id)
                        );

                        return [...updatedCart, ...newItems];
                    });

                }
            })
            .catch(error => {
                console.error(error);
            })
    };

    useEffect(() => {
        if (tableId) {
            fetchOrder(tableId);
        }
    }, [tableId]);

    useEffect(() => {
        const updateHeight = () => {
            const headerHeight = document.querySelector(".fix")?.clientHeight || 0;
            const footerHeight = document.querySelector(".footer")?.clientHeight || 0;
            setContentHeight(window.innerHeight - (headerHeight + footerHeight));
        };

        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, []);

    useEffect(() => {
        if (!tableId) return;

        const payload = {
            tableId,
            colorStatus: ["support", "newOrder"],
        };

        socket.emit(
            SocketEvent.NOTIFICATION_READ,
            payload,
            (response: { success: boolean }) => {
                if (response.success) {
                    fetchProduct();
                }
            }
        );
    }, [tableId]);

    useEffect(() => {
        socket.on(SocketEvent.WAITER_RECEIVE_PAYMENT, (data) => {
            if (data.success) {
                fetchProduct();
            }
        });

        return () => {
            socket.off(SocketEvent.WAITER_RECEIVE_PAYMENT);
        };
    }, []);

    useEffect(() => handleSocketEvent(SocketEvent.WAITER_RECEIVE_PAYMENT, t('paymentCompletedAtTable'), sound), []);
    useEffect(() => handleSocketEventCustomer(SocketEvent.WAITER_SUPPORT_CUSTOMER, t('customerNeedsSupportAtTable'), sound), []);
    useEffect(() => handleSocketEventCustomer(SocketEvent.WAITER_RECEIVE_ORDER_FROM_CUSTOMER, t('youHaveANewOrderAtTable'), sound), []);
    useEffect(() => handleSocketEventCustomer(SocketEvent.FREE_ITEM_REQUEST, t('youHaveANewOrderAtTable'), sound), []);

    useEffect(() => handleSocketStatusProductIn(SocketEvent.REJECT_ORDER, sound, false), []);
    useEffect(() => handleSocketStatusProductIn(SocketEvent.COOK_ORDER, sound, false), []);
    useEffect(() => handleSocketStatusProductIn(SocketEvent.READy_ORDER, sound, true), []);

    return (
        <div className="h-screen flex flex-col bg-[#F1F2F5] w-full relative">
            <TopBar />
            {
                !loading && shop &&
                <div
                    className="md:hidden absolute bottom-32 right-4 bg-gray-300 shadow-qty z-20 rounded-full p-2"
                    onClick={() => setIsCart(true)}
                >
                    <HiOutlineShoppingCart
                        className="text-primary relative"
                        size={20}
                    />
                    {
                        totalQuantity > 0 &&
                        <span
                            className={`absolute top-[-10px] right-0 bg-red-500 text-white text-[10px] rounded-full px-1.5`}
                        >
                            {totalQuantity}
                        </span>
                    }
                </div>
            }

            <div className="fix h-[70px] flex items-center px-2">
                <div className="flex justify-between items-center bg-transparent w-full">
                    <div className="flex items-center gap-2">
                        <div
                            className="px-4 py-1 md:py-2 bg-white rounded-md"
                            onClick={orderBack}
                        >
                            <HiOutlineArrowSmLeft size={24} />
                        </div>
                        <h3 className="text-base md:text-2xl font-medium">{t('order')}</h3>
                    </div>
                    <div className="px-4 py-2 rounded-full text-gray-500 bg-white">{tableName}</div>
                </div>
            </div>

            <div className="flex-grow" style={{ height: contentHeight }}>
                {
                    shop ?
                        <>
                            <div className="hidden md:block ">
                                <div className="grid grid-cols-12 gap-4 h-full">
                                    <div className="col-span-6 lg:col-span-7 xl:col-span-8 2xl:col-span-9 text-white h-full flex flex-col">
                                        <ListProduct tableId={tableId ? tableId : ""} setIsCate={setIsCate} />
                                    </div>
                                    <div
                                        style={{ height: contentHeight - 80 }}
                                        className="col-span-6 lg:col-span-5 xl:col-span-4 2xl:col-span-3 text-white bg-white rounded-[11px] h-full flex flex-col w-full relative"
                                    >
                                        <ListOrder fetchOrderByTableId={() => tableId && fetchOrder(tableId)} orderObj={orderObj} tableId={tableId ? tableId : ""} isCate={isCate} />
                                    </div>
                                </div>
                            </div>il
                            <div className="block md:hidden">
                                <Homepage
                                    tableId={tableId ? tableId : ""}
                                    setIsCate={setIsCate}
                                />
                            </div>
                        </>
                        :
                        <div className="h-[40vh] flex items-center justify-center">
                            <div className="flex flex-col items-center">
                                <div className="min-h-[80] min-w-[80] max-w-[80]">
                                    <img
                                        src={empty}
                                        className="rounded-[10px] object-cover h-[80] w-[80px]"
                                        alt="Asian Food"
                                    />
                                </div>
                                <p className="text-[16px] font-medium text-[#767676] pt-4">{t('invalidData')}</p>
                            </div>
                        </div>
                }
            </div>
            <CartDrawer
                isOpen={isCart}
                onClose={() => setIsCart(false)}
                tableId={tableId ? tableId : ""}
                contentHeight={contentHeight}
                fetchOrderByTableId={() => tableId && fetchOrder(tableId)}
                orderObj={orderObj}
                isCate={isCate}
            />
        </div>
    );
};

export default OrderByPOS;
